import router from '@/router'
import { setBackRouteToLastVisited } from '@/router/backRoute'
import { resolveRoute } from '@/router/resolve'
import { useAuthStore } from '@/store/pinia/auth'
import { useCustomersStore } from '@/store/pinia/customers'
import { useDevStore } from '@/store/pinia/dev'
import { useLayoutStore } from '@/store/pinia/layout'
import { useOrganizationStore } from '@/store/pinia/organization'
import { useBillingStore } from '@/store/pinia/organization/billing'
import { useCatalogRepositoryStore } from '@/store/pinia/organization/catalogRepository'
import { useCloudCostManagementStore } from '@/store/pinia/organization/cloudCostManagement'
import { useInventoryStore } from '@/store/pinia/organization/inventory'
import { useLicenceStore } from '@/store/pinia/organization/licence'
import { useNavConfigStore } from '@/store/pinia/organization/navConfig'
import { useProjectStore } from '@/store/pinia/organization/project'
import { useRootStore } from '@/store/pinia/root'
import { MENU_SECTIONS, MENU_TAB_ACTIONS, firstVisibleMenuSection } from '@/utils/config/menuSections'
import { getValidOrganization } from '@/utils/helpers'
import { buildNavSections } from '@/utils/placements'
import { checkUserPermissions, checkUserRoutePermissions, getRouteRequiredActions, isActionDenied, isSatisfiedByProjectOwnership } from '@/utils/plugins/permissions'
import { pinia } from '@/utils/plugins/pinia'
import { useGtm } from '@gtm-support/vue-gtm'

export const handleDevLayerChanges = () => (_to, _from) => {
  const devStore = useDevStore(pinia)
  devStore.RESET_DEV_STATE('mockedEndpointsCalled')
  devStore.RESET_DEV_STATE('permissionsChecked')
}

/** Determines the organization relationship when the user copy/pastes the url or visits the page fresh */
export const checkOrgOrigin = () => async (to, from) => {
  if (isLoggingOut(to) || isNotFound(to)) return

  const authStore = useAuthStore(pinia)
  const orgStore = useOrganizationStore(pinia)
  const rootStore = useRootStore(pinia)

  const orgCanonical = to?.params?.orgCanonical ?? authStore.jwtDecoded?.organization?.canonical
  const isOrgDependentRoute = to.path.includes('organizations')
  const hasOrgCanonical = isOrgDependentRoute && !_.isEmpty(orgCanonical)

  if (!hasOrgCanonical) return

  const isPastedUrl = _.isNil(from.name)
  const isOrgMismatch = orgCanonical !== orgStore.orgCanonical

  if (!isPastedUrl && !isOrgMismatch) return

  if (isPastedUrl) await rootStore.FETCH_ORGS()

  const isOrgInOrgsList = _.some(rootStore.organizations, ['canonical', orgCanonical])
  if (isOrgInOrgsList) {
    const parentOrgCanonical = authStore.jwtDecoded?.parent
    await orgStore.CHANGE_ORGANIZATION({ nextCanonical: orgCanonical, parentCanonical: parentOrgCanonical })
    return
  }

  const customersStore = useCustomersStore(pinia)
  const switched = await customersStore.CHANGE_ORG_SCOPE({ nextCanonical: orgCanonical })
  if (!switched) {
    return { name: 'notFound', state: { 0: to.fullPath }, replace: true }
  }

  // CHANGE_ORG_SCOPE leaves scope bookkeeping to its caller (LOGIN_AS_* actions each do their own).
  // This is a fresh entry into the org, not a nested customer login, so reconcile scopes[0] the same
  // way CHANGE_ORGANIZATION's _performOrgSwitch does for a top-level switch: without this, a later
  // no-arg REFRESH_TOKEN (auto-refresh interceptor) rebuilds its query from the now-stale scopes[0]
  // and silently reverts the session to the previous org (ENG-307).
  customersStore.SET_SCOPE_DEPTH(0)
  customersStore.REMOVE_CUSTOMER_SCOPES()
  customersStore.SET_CURRENT_SCOPE({ canonical: orgCanonical, name: orgStore.orgName, jwt: authStore.jwt })
}

export const trackNextPrivatePageToRedirectAfterLogIn = () => (to, _from) => {
  if (isLoggingOut(to)) return

  if (!to.meta.isPublic) {
    const authStore = useAuthStore(pinia)
    const { path, name, hash, params, query } = to
    authStore.SET_DEFAULT_ROUTE({ path, name, hash, query, params })
  }
}

export const checkOrgBillingState = () => (to, _from) => {
  if (isLoggingOut(to)) return
  const billingStore = useBillingStore(pinia)
  const orgStore = useOrganizationStore(pinia)
  const rootStore = useRootStore(pinia)

  const { hasActiveBillingPlan } = billingStore
  const { orgCanonical } = orgStore

  // Only org admins can act on billing, and only they can reach orgSettings (ENG-287) —
  // nudging anyone else there would just dead-end them in a forbidden redirect.
  if (!hasActiveBillingPlan && rootStore.isOrgAdmin && !isAccessingAllowedPage({ to })) {
    return { name: 'orgSettings', params: { orgCanonical } }
  }
}

/**
 * Route locations a section navigation can land on: its CyPageTabs tabs, or the section
 * route itself. Tabs are read from the section route's own record (`matched.at(-1)`, like
 * CyMainNav) and not from the resolved route's merged meta: the merge inherits the parent
 * `organization` route's tabs, which would wrongly gate tab-less sections (plugin widgets)
 * behind the org-settings tabs.
 */
const getMenuSectionLandingRoutes = (section, params) => {
  const sectionParams = section.params ?? params
  const tabs = resolveRoute({ name: section.name, params: sectionParams })?.matched?.at(-1)?.meta?.tabs?.() ?? []
  const landingRouteNames = _.isEmpty(tabs) ? [section.name] : tabs
  return landingRouteNames.map((name) => ({ name, params: sectionParams }))
}

/**
 * Central gate for the menu:dashboard permission. Every entry point (sidebar, logo,
 * post-login redirect, NotFound/Forbidden fallbacks, breadcrumbs, direct URL) navigates to
 * `{ name: 'dashboard' }`, so gating the route here catches them all in one place: when the
 * user can't see the dashboard, send them to the first available sidebar element in the
 * order the user sees it (native and plugin-widget sections interleaved by their navConfig
 * positions, same source as CyMainNav), falling back to the org picker only when nothing
 * qualifies.
 *
 * The placements runtime only registers widgets for the current route's org, so the guard
 * syncs the target org's placements itself before deciding; without that, redirecting to
 * `pluginWidget` would 404 in `ensurePluginWidgetExists`. `syncOrgPlacements` is imported
 * dynamically because a static import would create a module cycle (router/index >
 * globalGuards > plugins/placements > router/index) and would pull the widget components
 * into the router chunk for a path most sessions never hit.
 */
export const redirectDashboardWhenDenied = () => async (to, _from) => {
  if (to.name !== 'dashboard') return

  const authStore = useAuthStore(pinia)
  const canonicals = to.params ?? {}
  await authStore.EVALUATE_USER_ACTIONS({
    actions: MENU_SECTIONS.map(({ menuAction }) => menuAction),
    canonicals,
  })

  if (!isActionDenied('GetMenuDashboard')) return

  const orgCanonical = to.params?.orgCanonical || useOrganizationStore(pinia).orgCanonical

  const navConfigStore = useNavConfigStore(pinia)
  const { syncOrgPlacements } = await import('@/utils/plugins/placements')
  const [, arePlacementsSynced] = await Promise.all([
    navConfigStore.FETCH_NAV_CONFIG({ orgCanonical }),
    syncOrgPlacements(orgCanonical),
  ])

  const navSections = buildNavSections({ orgCanonical, positionFor: navConfigStore.positionFor })
    .filter((section) => arePlacementsSynced || !section.widgetSlug)

  // menu:* visibility doesn't imply route access: each landing tab has its own requiredActions
  // plus its submenu menu:* action, and CyPageTabs forwards to /forbidden when none passes.
  // Evaluate those actions (only on this rare denied path) so the redirect never targets a
  // dead end.
  const candidateSections = navSections.filter(({ menuAction }) => !isActionDenied(menuAction))
  const landingActions = _(candidateSections)
    .flatMap((section) => getMenuSectionLandingRoutes(section, canonicals))
    .flatMap((landingRoute) => [
      ...(MENU_TAB_ACTIONS[landingRoute.name] ? [MENU_TAB_ACTIONS[landingRoute.name]] : []),
      ...getRouteRequiredActions(router, landingRoute),
    ])
    .uniq()
    .value()
  if (!_.isEmpty(landingActions)) await authStore.EVALUATE_USER_ACTIONS({ actions: landingActions, canonicals })

  const isMenuTabDenied = (routeName) => !!MENU_TAB_ACTIONS[routeName]
    && isActionDenied(MENU_TAB_ACTIONS[routeName])
  const canAccessMenuSection = (section) => getMenuSectionLandingRoutes(section, canonicals)
    .some((landingRoute) => !isMenuTabDenied(landingRoute.name) && checkUserRoutePermissions(router, landingRoute))

  const section = firstVisibleMenuSection(isActionDenied, canAccessMenuSection, navSections)
  return section
    ? { name: section.name, params: section.params ?? { orgCanonical } }
    : { name: 'organizations' }
}

export const checkAuthBeforeEach = () => async (to, from) => {
  // Special cases: logging out or already authenticated user
  const authStore = useAuthStore(pinia)

  if (to.name === 'logout' || authStore.isAuthenticated) {
    return
  }

  const rootStore = useRootStore(pinia)
  const orgStore = useOrganizationStore(pinia)
  // Attempt to restore session
  await rootStore.RESTORE_SESSION()

  const { isAuthenticated } = authStore

  const { isPublic = false } = to.meta

  // Case 1: User is authenticated after session restoration
  if (isAuthenticated) {
    // If trying to access public login/signup pages - redirect to dashboard
    if (isPublic && ['login', 'signUp'].includes(to.name)) {
      // If user has only token, prefetch orgs
      if (_.isEmpty(rootStore.organizations)) {
        await rootStore.FETCH_ORGS()
      }
      const { orgCanonicalToLogIn } = to.params || {}
      const orgCanonical = orgStore.orgCanonical || getValidOrganization(rootStore.organizations, orgCanonicalToLogIn).canonical
      return { name: 'dashboard', params: { orgCanonical } }
    }
    // Otherwise allow the transition
    return
  }

  // Case 2: User is not authenticated

  // If this is a public page
  if (isPublic) {
    // 404 page for unauthenticated users - redirect to login
    if (isNotFound(to)) {
      return { name: 'login' }
    }

    // Prevent infinite loop when transitioning from login to login
    if (from.name === 'login' && to.name === 'login') {
      return false
    }

    // Allow access to public pages (including login)
    return
  }

  // Case 3: Unauthenticated user trying to access private page
  // Redirect to login page
  return { name: 'login' }
}

const SECTION_STORE_RESETS = {
  cloudCostManagementSection: ({ ccm }) => ccm.RESET_CE_STATE(),
  projects: ({ project }) => project.RESET_PROJ_STATE(),
  environments: ({ project }) => project.RESET_PROJ_STATE(),
  orgEnvironmentSection: ({ project }) => project.RESET_PROJ_STATE(),
  orgEnvironmentSectionOverview: ({ project }) => project.RESET_PROJ_STATE(),
  orgEnvironmentSectionSettings: ({ project }) => project.RESET_PROJ_STATE(),
  inventory: ({ inventory }) => inventory.RESET_INVENTORY_STATE(),
  resources: ({ inventory }) => inventory.RESET_INVENTORY_STATE(),
  catalogRepositories: ({ catalog }) => catalog.RESET_CR_STATE(),
  stacks: ({ catalog }) => catalog.RESET_CR_STATE(),
}

export const resetScopedStores = () => (to, from) => {
  const { name: fromName } = from || {}
  const isRefreshing = sessionStorage.isRefreshing || false
  const isPageLoad = _.isEmpty(fromName)
  const fromSection = _.get(from, 'meta.appSection')
  const toSection = _.get(to, 'meta.appSection')
  const isChangingScope = !isPageLoad && fromSection !== toSection
  if (!isChangingScope || isRefreshing) return

  const layoutStore = useLayoutStore(pinia)
  if (_.has(layoutStore.dataTables, fromName)) {
    layoutStore.RESET_DATA_TABLE_SEARCH({ name: fromName })
    layoutStore.RESET_DATA_TABLE_PAGE({ name: fromName })
    layoutStore.RESET_DATA_TABLE_FILTERS({ name: fromName })
  }

  const orgStore = useOrganizationStore(pinia)
  orgStore.RESET_ORG_STATE('available')

  const reset = SECTION_STORE_RESETS[fromSection]
  if (reset) {
    reset({
      ccm: useCloudCostManagementStore(pinia),
      project: useProjectStore(pinia),
      inventory: useInventoryStore(pinia),
      catalog: useCatalogRepositoryStore(pinia),
    })
  }
}

export const evaluateUserActions = () => async (to, from) => {
  const { actions = {}, requiredActions = [], isAlpha } = to.meta || {}
  // Pre-fetch: evaluate requiredActions before server fetch to know which actions to request.
  // This call may return stale results if evaluatedActions is not yet populated (cold nav).
  const actionsToFetch = _.isFunction(requiredActions) ? requiredActions() : requiredActions

  if (isLoggingOut(to) || to.meta.isPublic) return

  if (_.some([actions, actionsToFetch], (acts) => !_.isEmpty(acts))) {
    const uniqueActions = _.uniq([...Object.values(actions), ...actionsToFetch])
    const canonicals = to.params ?? {}
    const authStore = useAuthStore(pinia)
    await authStore.EVALUATE_USER_ACTIONS({ actions: uniqueActions, canonicals })
  }

  if (to.name === 'organizations') return

  // Post-fetch: re-evaluate requiredActions so dynamic functions read the now-warm cache.
  const computedRequiredActions = _.isFunction(requiredActions) ? requiredActions() : actionsToFetch

  const { backRouteTo, permissionsKey } = _.cloneDeep(to.meta)
  // A component write route is reachable by the owner of the project it targets even without the
  // component permission, mirroring the backend owner fallback (BE-473).
  const succeededReqActionCheck = isAlpha || _(computedRequiredActions)
    .map((requiredAction) => checkUserPermissions(requiredAction) || isSatisfiedByProjectOwnership(requiredAction, to.params))
    .every()

  if (!succeededReqActionCheck) {
    return {
      name: 'forbidden',
      params: { permissionsKey },
      replace: true,
      state: { backRouteTo, 0: to.fullPath },
    }
  }
}

export const clearIsRefreshing = () => (to, _from) => {
  if (isLoggingOut(to)) return

  sessionStorage.removeItem('isRefreshing')
}

export const getLatestStatus = () => async (to, _from) => {
  const rootStore = useRootStore(pinia)
  if (isLoggingOut(to)) return

  await rootStore.FETCH_SERVICES_STATUS()
}

export const handleOrganizationLicenceAuthorization = ({ getGtm = useGtm } = {}) => async (to, _from) => {
  const orgStore = useOrganizationStore(pinia)
  const custStore = useCustomersStore(pinia)
  const gtm = getGtm()

  if (isLoggingOut(to) || !orgStore.isOrgSelected || isNotFound(to) || isForbidden(to)) return
  const licenceStore = useLicenceStore(pinia)

  if (!licenceStore.isFetched) await licenceStore.GET_LICENCE()

  // Enable analytics only for non-on-prem organizations
  // GTM is disabled by default and only enabled after confirming the license type
  if (!_.isEmpty(import.meta.env.VUE_APP_GTM_ID)) {
    const shouldEnableAnalytics = !licenceStore.isOnPrem
    gtm?.enable(shouldEnableAnalytics)
  }

  const { orgCanonical } = orgStore
  const hasValidOrgLicence = licenceStore.hasLicence && !licenceStore.isExpired

  if (hasValidOrgLicence || _.isEmpty(orgCanonical) || isAccessingAllowedPage({ to })) return

  // Only org admins can act on a licence, and only they can reach orgSettings (ENG-287) —
  // nudging anyone else there would just dead-end them in a forbidden redirect.
  if (!useRootStore(pinia).isOrgAdmin) return

  // A child org inherits its ancestors' licence and must never be forced to own one.
  // Scope depth alone can't identify a root org: CHANGE_ORGANIZATION resets the depth
  // to 0 when switching to a member child org (dropdown switch, page refresh), which
  // used to redirect child-org dashboards to orgSettings on reload (ENG-217).
  const isRootOrg = custStore.currentScopeDepth === 0 && _.isEmpty(orgStore.detail?.parent_canonical)
  const shouldRedirect = to.name !== 'orgSettings' && (licenceStore.isExpired || (isRootOrg && !licenceStore.hasLicence))

  if (shouldRedirect) return { name: 'orgSettings', params: { orgCanonical } }
}

export const trackNavigationHistory = () => (to, from) => {
  if (isLoggingOut(to) || from?.meta?.isPublic) return
  setBackRouteToLastVisited(to, from)
}

export function isLoggingOut (to) {
  return ['logout', 'login'].includes(to.name)
}

export function isNotFound (to) {
  return ['notFound'].includes(to.name)
}

export function isForbidden (to) {
  return ['forbidden'].includes(to.name)
}

/** Returns true if accessing an allowed page (not limited by licence or plan subscription) */
export function isAccessingAllowedPage ({ to }) {
  const isRoutePublic = to?.meta?.isPublic ?? false
  const isAllowedPage = ['organizations', 'profile', 'orgSettings', 'newOrganization'].includes(to.name)
  return (isRoutePublic && !isForbidden(to)) || isAllowedPage
}

export default function globalGuards () {
  return {
    beforeEach: [
      handleDevLayerChanges(),
      trackNextPrivatePageToRedirectAfterLogIn(),
      checkAuthBeforeEach(),
      checkOrgOrigin(),
      redirectDashboardWhenDenied(),
      checkOrgBillingState(),
      evaluateUserActions(),
      clearIsRefreshing(),
      getLatestStatus(),
      handleOrganizationLicenceAuthorization(),
    ],
    afterEach: [
      resetScopedStores(),
      trackNavigationHistory(),
    ],
  }
}
